import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  normalizeFilterableSettings,
} from 'core/Filters/util';
import {
  MarkRTMPatientReviewedRequestDTO,
  None,
  PostApiRTMMonitoringSendCheckInSurveyByPatientIdParams,
  PostApiRTMMonitoringSendEnrollmentInvitationByPatientIdParams,
  RTMPatientStatusResponseDTO,
  RTMPatientTrendResponseDTO,
  RTMProviderThresholdsDTO,
} from 'dtos';
import {
  SettingsDTO,
} from 'dtos/settingsDTO';
import {
  getApiRTMGetStatusByPatientId,
  getApiRTMMonitoringGetPatientTrend,
  getApiRTMMonitoringGetProviderThresholds,
  getApiSettingsGetRTMMonitoringFilterSettings,
  postApiRTMMonitoringAddOrUpdateProviderThresholds,
  postApiRTMMonitoringMarkPatientReviewed,
  postApiRTMMonitoringSendCheckInSurveyByPatientId,
  postApiRTMMonitoringSendEnrollmentInvitationByPatientId,
} from 'endpoints';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';

export function useRTMMonitoringFilterMetaComponents() {
  const { data } = usePrefetchedData<SettingsDTO>({
    key: ['rtm-monitoring-filter-settings'],
    fetchFn: getApiSettingsGetRTMMonitoringFilterSettings,
  });

  return normalizeFilterableSettings(data?.filterableSettings ?? []);
}

export function useMarkPatientReviewed(): UseMutationResult<
  None,
  Error,
  MarkRTMPatientReviewedRequestDTO
> {
  return useMutation<None, Error, MarkRTMPatientReviewedRequestDTO>({
    mutationFn: (data) => postApiRTMMonitoringMarkPatientReviewed(data),
  });
}

export function useSendCheckInSurvey(): UseMutationResult<
  None,
  Error,
  PostApiRTMMonitoringSendCheckInSurveyByPatientIdParams
> {
  return useMutation<None, Error, PostApiRTMMonitoringSendCheckInSurveyByPatientIdParams>({
    mutationFn: (params) => postApiRTMMonitoringSendCheckInSurveyByPatientId(params),
  });
}

export function useSendEnrollmentInvitation(): UseMutationResult<
  None,
  Error,
  PostApiRTMMonitoringSendEnrollmentInvitationByPatientIdParams
> {
  return useMutation<None, Error, PostApiRTMMonitoringSendEnrollmentInvitationByPatientIdParams>({
    mutationFn: (params) => postApiRTMMonitoringSendEnrollmentInvitationByPatientId(params),
  });
}

export function useGetRTMPatientStatus(
  patientId: number,
  enabled = true,
): UseQueryResult<RTMPatientStatusResponseDTO, Error> {
  return useQuery<RTMPatientStatusResponseDTO, Error>({
    queryKey: ['rtm-patient-status', patientId],
    queryFn: () => getApiRTMGetStatusByPatientId({ patientId }),
    enabled: enabled && patientId > 0,
  });
}

export function useGetPatientTrend(
  patientId: number,
  enabled = true,
): UseQueryResult<RTMPatientTrendResponseDTO, Error> {
  return useQuery<RTMPatientTrendResponseDTO, Error>({
    queryKey: ['rtm-patient-trend', patientId],
    queryFn: () => getApiRTMMonitoringGetPatientTrend({ patientId }),
    enabled: enabled && patientId > 0,
  });
}

export function useGetProviderThresholds(
  providerUserId: number,
  enabled = true,
): UseQueryResult<RTMProviderThresholdsDTO, Error> {
  return useQuery<RTMProviderThresholdsDTO, Error>({
    queryKey: ['rtm-provider-thresholds', providerUserId],
    queryFn: () => getApiRTMMonitoringGetProviderThresholds({ providerUserId }),
    enabled: enabled && providerUserId > 0,
  });
}

export function useAddOrUpdateProviderThresholds(): UseMutationResult<
  RTMProviderThresholdsDTO,
  Error,
  RTMProviderThresholdsDTO
> {
  return useMutation<RTMProviderThresholdsDTO, Error, RTMProviderThresholdsDTO>({
    mutationFn: (data) => postApiRTMMonitoringAddOrUpdateProviderThresholds(
      data,
      { params: { providerUserId: data.providerUserId } },
    ),
  });
}
